import {
  createSelector,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import type { BriefNote, BriefId, BriefProjectId } from 'types/brief'
import { createAppSlice } from '@disco/utils/slices'
import {
  createBriefNote as createBriefNoteApi,
  updateBriefNote as updateBriefNoteApi,
  deleteBriefNote as deleteBriefNoteApi,
} from '@disco/api/briefs'
import type {
  CreateBriefNotePayload,
  UpdateBriefNotePayload,
} from '@disco/api/briefs'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import captureError from '@disco/utils/captureError'

// Extended BriefNote type for state management
type BriefNoteWithState = BriefNote & {
  deleting?: boolean
  updating?: boolean
  creating?: boolean
}

export interface BriefNotesState {
  loading: boolean
  loaded: boolean
  error: SerializedError | null
  notes: Record<number, BriefNoteWithState>
}

const initialState: BriefNotesState = {
  loading: false,
  loaded: false,
  error: null,
  notes: {},
}

const briefNotesSlice = createAppSlice({
  name: 'briefNotes',
  initialState,
  reducers: create => ({
    createBriefNote: create.asyncThunk<
      BriefNote,
      CreateBriefNotePayload,
      { rejectValue: { error: string } }
    >(
      async (payload, thunkApi) => {
        try {
          const newNote = await createBriefNoteApi(payload)
          pushUINotification({
            title: 'Note created!',
            message: 'Your note has been created successfully.',
            level: 'success',
          })
          return newNote
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: state => {
          state.loading = true
          state.loaded = false
        },
        fulfilled: (state, action: PayloadAction<BriefNote>) => {
          state.loading = false
          state.loaded = true
          state.error = null
          state.notes[action.payload.id] = action.payload
        },
        rejected: (
          state,
          action: PayloadAction<unknown, string, any, SerializedError>,
        ) => {
          state.loading = false
          state.error = action.error
        },
      },
    ),
    updateBriefNote: create.asyncThunk<
      BriefNote,
      { noteId: number; payload: UpdateBriefNotePayload },
      { rejectValue: { error: string } }
    >(
      async ({ noteId, payload }, thunkApi) => {
        try {
          const updatedNote = await updateBriefNoteApi(noteId, payload)
          pushUINotification({
            title: 'Note updated!',
            message: 'Your note has been updated successfully.',
            level: 'success',
          })
          return updatedNote
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const note = state.notes[meta.arg.noteId]
          if (note) {
            state.notes[meta.arg.noteId] = {
              ...note,
              updating: true,
            }
          }
        },
        fulfilled: (state, action: PayloadAction<BriefNote>) => {
          const note = state.notes[action.payload.id]
          if (note) {
            state.notes[action.payload.id] = {
              ...action.payload,
              updating: false,
            }
          }
        },
        rejected: (state, { meta, payload }) => {
          const note = state.notes[meta.arg.noteId]
          if (note) {
            state.notes[meta.arg.noteId] = {
              ...note,
              updating: false,
            }
          }
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    deleteBriefNote: create.asyncThunk<
      number,
      number,
      { rejectValue: { error: string } }
    >(
      async (noteId, thunkApi) => {
        try {
          await deleteBriefNoteApi(noteId)
          pushUINotification({
            title: 'Note deleted!',
            message: 'Your note has been deleted successfully.',
            level: 'success',
          })
          return noteId
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const note = state.notes[meta.arg]
          if (note) {
            state.notes[meta.arg] = { ...note, deleting: true }
          }
        },
        fulfilled: (state, action: PayloadAction<number>) => {
          delete state.notes[action.payload]
        },
        rejected: (state, { meta, payload }) => {
          const note = state.notes[meta.arg]
          if (note) {
            state.notes[meta.arg] = { ...note, deleting: false }
          }
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
  }),
  extraReducers: builder => {
    // Populate briefNotes store when loading briefs or projects
    builder.addMatcher(
      action =>
        action.type === 'briefs/loadBrief/fulfilled' ||
        action.type === 'briefProjects/loadBriefProject/fulfilled',
      (state, action: any) => {
        const entity = action.payload
        if (entity.associated_notes && Array.isArray(entity.associated_notes)) {
          entity.associated_notes.forEach(note => {
            state.notes[note.id] = note
          })
        }
      },
    )
  },
})

export const { createBriefNote, updateBriefNote, deleteBriefNote } =
  briefNotesSlice.actions

// Selectors
export const getBriefNoteById = (
  state,
  { noteId }: { noteId: number },
): BriefNoteWithState | undefined => {
  return state.entities.briefNotes.notes[noteId]
}

export const getAllBriefNotes = createSelector(
  [state => state.entities.briefNotes.notes],
  notes => Object.values(notes),
)

export const getBriefNotesForBrief = createSelector(
  [
    state => state.entities.briefNotes.notes,
    (state, { briefId }: { briefId: BriefId }) => briefId,
  ],
  (
    notes: Record<number, BriefNoteWithState>,
    briefId: BriefId,
  ): BriefNoteWithState[] =>
    Object.values(notes)
      .filter((note: BriefNoteWithState) => note.for_brief === briefId)
      .sort((a, b) => a.id - b.id),
)

export const getBriefNotesForProject = createSelector(
  [
    state => state.entities.briefNotes.notes,
    (state, { briefProjectId }: { briefProjectId: BriefProjectId }) =>
      briefProjectId,
  ],
  (
    notes: Record<number, BriefNoteWithState>,
    briefProjectId: BriefProjectId,
  ): BriefNoteWithState[] =>
    Object.values(notes)
      .filter((note: BriefNoteWithState) => note.for_project === briefProjectId)
      .sort((a, b) => a.id - b.id),
)

export const getBriefNotesLoading = (state): boolean => {
  return state.entities.briefNotes.loading
}

export const getBriefNotesLoaded = (state): boolean => {
  return state.entities.briefNotes.loaded
}

export const getBriefNotesError = (state): SerializedError | null => {
  return state.entities.briefNotes.error
}

export const briefNotesReducer = briefNotesSlice.reducer
